/**
 * Seed Data Loader
 *
 * Populates localStorage with expanded seed data on first load.
 * Since Firebase may not be configured, this writes directly to
 * localStorage in the same format DataService uses for its fallback.
 *
 * Called once on app init from main.jsx.
 */
import {
  EXPANDED_CUSTOMERS, EXPANDED_CARRIERS, EXPANDED_LOADS, EXPANDED_QUOTES,
  EXPANDED_PURCHASE_ORDERS, EXPANDED_APPOINTMENTS, EXPANDED_CARRIER_VETTING,
  EXPANDED_IOT_TRACKERS, EXPANDED_IOT_EVENTS, EXPANDED_ASSETS, EXPANDED_TRUCKS,
  EXPANDED_FINANCIALS, EXPANDED_EVENTS, EXPANDED_EXCEPTIONS, SEED_SUMMARY,
} from './seedDataExpanded';
import { DEMO_TENANT_ID, WATCHMEN_TENANT_ID, SEED_VERSION } from './tenantDefaults';
import { FORCE_RESEED_ALLOWED } from './seedAllowlist';
import {
  SEED_FLEET_VEHICLES, SEED_FLEET_DRIVERS, SEED_FLEET_MAINTENANCE, SEED_FLEET_COMPLIANCE,
} from './fleetSeed';
import { SEED_AEGIS_ACTIONS, SEED_AEGIS_PREDICTIONS } from './aegisSeed';
import { SEED_AEGIS_AGENTS, SEED_AGENT_MEMORY, SEED_AEGIS_AGENT_EVENTS, SEED_AEGIS_NEGOTIATIONS } from './swarmSeed';
import { SEED_VOICE_CALLS } from './voiceSeed';
import { SEED_AEGIS_RISK_EVENTS, SEED_AEGIS_COUNTRY_RISK } from './riskSeed';
import {
  SEED_AEGIS_USER_ACTIVITY,
  SEED_AEGIS_PERFORMANCE_PROFILES,
  SEED_AEGIS_COACHING_INSIGHTS,
  SEED_AEGIS_PERFORMANCE_GOALS,
} from './performanceSeed';
import {
  SEED_CARRIER_COMPLIANCE,
  SEED_QUOTE_OUTCOMES,
  SEED_QUOTE_TEMPLATES,
} from './nqlSeed';
import {
  SEED_DOC_PARSE_JOBS,
  SEED_DOC_PARSE_RESULTS,
  SEED_DOC_FEEDBACK,
} from './docSeed';
import {
  SEED_RIVERSIDE_LOADS,
  SEED_RIVERSIDE_PICKS,
} from './docRiversideSeed';
import {
  SEED_KEYSTON_PARSE_JOBS,
  SEED_KEYSTON_PARSE_RESULTS,
} from './docKeystonSeed';
import {
  SEED_VOICE_LOADS,
  SEED_VOICE_CUSTOMERS,
  SEED_VOICE_INVENTORY,
  SEED_VOICE_PURCHASE_ORDERS,
  SEED_VOICE_EXCEPTIONS,
} from './voiceScenarioSeed';
import {
  SEED_RATE_CONFIRMATIONS, SEED_CARRIER_INVOICES, SEED_DISPUTES,
  SEED_CONSOLIDATION_OPPORTUNITIES,
  SEED_EMISSIONS_FACTORS, SEED_EMISSIONS_REPORTS,
  SEED_SCENARIOS,
  SEED_CUSTOMS_DOCUMENTS, SEED_MULTIMODAL_LOADS,
} from './tmsOperationsSeed';
// TMS-AUTOSEED-SEAT-F-PARITY-1 — canonical spot_bids for the TMS-C3 chain
import { SEED_SPOT_BIDS } from './seatFParitySeed';
import {
  SEED_CARRIER_ALLOCATION_RULES, SEED_REPORT_SCHEDULES, SEED_REPORT_RUNS,
  SEED_EDI_MESSAGES, SEED_CARRIER_ONBOARDING_APPLICATIONS,
  SEED_LOADBOARD_POSTINGS, SEED_LOADBOARD_QUOTES,
  SEED_CARRIER_RATE_TABLES, SEED_CARRIER_CONTRACTS,
  SEED_RATE_CONFIRMATIONS_TMS11,
  SEED_LTL_RATE_QUOTES, SEED_LTL_PRO_TRACKING,
} from './tmsCarrierCustomerSeed';
import {
  SEED_NPS_SURVEYS, SEED_CSAT_SURVEYS,
  SEED_CUSTOMER_ONBOARDING_APPS, SEED_CUSTOMER_CONTRACTS,
} from './cxSeed';
import {
  SEED_CRM_ACCOUNTS, SEED_CRM_CONTACTS, SEED_CRM_DEALS, SEED_CRM_ACTIVITIES,
  SEED_CRM_DEMO_SENDS, SEED_CRM_DEMO_ENGAGEMENT, SEED_CRM_PROSPECT_MIRRORS,
} from './crmSeed';
import {
  SEED_CONNECTORS, SEED_CONNECTOR_SYNC_LOGS, SEED_CONNECTOR_FIELD_MAPS,
} from './integrationsSeed';
import {
  SEED_CONSOLIDATION_ENGINE_OPPS,
  SEED_CONSOLIDATION_OUTCOMES,
} from './consolidationEngineSeed';
import {
  SEED_DISPATCH_AUTONOMY_RULES,
  SEED_AGENT_DISPATCH_LOG,
} from './dispatchAutonomySeed';
import {
  SEED_CARRIER_TRUST_SCORES,
  SEED_CARRIER_ALERTS,
  SEED_CARRIER_VETTING_HISTORY,
} from './carrierVettingEngineSeed';
import {
  SEED_EXCEPTION_PREDICTIONS,
  SEED_PROACTIVE_NOTIFICATIONS,
} from './agentStatusSeed';
import {
  SEED_DAILY_OPS_BRIEFS,
} from './dailyOpsSeed';
import {
  SEED_HS_CLASSIFICATIONS,
  SEED_TARIFF_ALERTS,
  SEED_LANDED_COST_SNAPSHOTS,
} from './tariffSeed';
import {
  SEED_QUOTING_RATE_TABLES,
  SEED_QUOTING_RATE_CONFIRMATIONS,
  SEED_QUOTING_INVOICES,
  SEED_QUOTING_HUNGER_LOADS,
  SEED_QUOTING_QUOTES,
} from './quotingIntelSeed';
import {
  SEED_CRUISE_SAILINGS,
  SEED_CRUISE_RECIPES,
  SEED_CRUISE_CONSUMPTION,
  SEED_CRUISE_PROVISIONING_PLANS,
  SEED_MARPOL_RECORDS,
} from './cruiseSeed';
import { seedComplianceData } from './complianceSeed';

// AGENT-MFG: Retailer compliance profiles (Home Depot, Costco, Walmart)
const SEED_RETAILER_COMPLIANCE_PROFILES = [
  {
    id: 'rcp-home-depot',
    retailerId: 'retailer-home-depot',
    name: 'Home Depot',
    chargebackRate: 0.03,
    mabdEnforcement: true,
    labelRequirements: ['UPC', 'carton-label-4x6', 'pallet-placard'],
    appointmentRequired: true,
    asnRequired: true,
    palletConfig: { maxHeight: 60, maxWeight: 1500, pattern: 'pinwheel' },
    tenantId: DEMO_TENANT_ID,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'rcp-costco',
    retailerId: 'retailer-costco',
    name: 'Costco',
    chargebackRate: 0.02,
    mabdEnforcement: true,
    labelRequirements: ['member-label', 'inner-pack-upc'],
    appointmentRequired: true,
    asnRequired: false,
    palletConfig: { maxHeight: 56, maxWeight: 1200, pattern: 'column', strapped: true },
    tenantId: DEMO_TENANT_ID,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'rcp-walmart',
    retailerId: 'retailer-walmart',
    name: 'Walmart',
    chargebackRate: 0.025,
    mabdEnforcement: true,
    labelRequirements: ['SSCC-18', 'GS1-128', 'walmart-item-label'],
    appointmentRequired: false,
    asnRequired: true,
    palletConfig: { maxHeight: 60, maxWeight: 1500, pattern: 'block' },
    tenantId: DEMO_TENANT_ID,
    createdAt: new Date().toISOString(),
  },
];

// AGENT-SPEC: Specifier demo projects (Keyston Bros use case)
const SEED_SPECIFIER_PROJECTS = [
  {
    id: 'sp-keyston-flooring-001',
    projectId: 'sp-keyston-flooring-001',
    specifierId: 'cust-keyston-001',
    projectName: 'Parkview Office Plaza — Phase 2 Flooring',
    architect: 'Harmon & Associates Architecture',
    specifiedItems: [
      {
        itemSku: 'SKU-CARPET-TILE-4200',
        specifiedQty: 2400,
        reservedQty: 800,
        substitutionLadder: ['SKU-CARPET-TILE-4210', 'SKU-CARPET-TILE-4215'],
        targetInstallDate: new Date(Date.now() + 45 * 86400000).toISOString(),
      },
      {
        itemSku: 'SKU-LVT-PLANK-710',
        specifiedQty: 1200,
        reservedQty: 1200,
        substitutionLadder: ['SKU-LVT-PLANK-720'],
        targetInstallDate: new Date(Date.now() + 45 * 86400000).toISOString(),
      },
      {
        itemSku: 'SKU-TRANSITION-STRIP-88',
        specifiedQty: 160,
        reservedQty: 80,
        substitutionLadder: [],
        targetInstallDate: new Date(Date.now() + 45 * 86400000).toISOString(),
      },
    ],
    status: 'active',
    targetInstallDate: new Date(Date.now() + 45 * 86400000).toISOString(),
    tenantId: DEMO_TENANT_ID,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'sp-arch-commercial-002',
    projectId: 'sp-arch-commercial-002',
    specifierId: 'cust-arch-002',
    projectName: 'Meridian Medical Center — Lobby & Corridor Refresh',
    architect: 'Rivera Commercial Design Group',
    specifiedItems: [
      {
        itemSku: 'SKU-BROADLOOM-COMML-55',
        specifiedQty: 3600,
        reservedQty: 3600,
        substitutionLadder: ['SKU-BROADLOOM-COMML-57', 'SKU-BROADLOOM-COMML-60'],
        targetInstallDate: new Date(Date.now() + 120 * 86400000).toISOString(),
      },
      {
        itemSku: 'SKU-RUBBER-BASE-2IN',
        specifiedQty: 800,
        reservedQty: 800,
        substitutionLadder: ['SKU-RUBBER-BASE-2IN-ALT'],
        targetInstallDate: new Date(Date.now() + 120 * 86400000).toISOString(),
      },
    ],
    status: 'active',
    targetInstallDate: new Date(Date.now() + 120 * 86400000).toISOString(),
    tenantId: DEMO_TENANT_ID,
    createdAt: new Date().toISOString(),
  },
];

const SEED_VERSION_KEY = 'wc_seed_version';

// Default tenant identities seeded on first load — the master registry.
const SEED_TENANTS = [
  {
    tenantId: DEMO_TENANT_ID,
    name: 'Kairos Demo',
    status: 'active',
    plan: 'enterprise',
    primaryContactName: 'Kairos Demo Admin',
    primaryContactEmail: 'demo@elyonsci.ai',
    primaryContactPhone: '',
    config: {
      timezone: 'America/Chicago',
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD',
      weightUnit: 'lbs',
      distanceUnit: 'miles',
      modules: ['overview', 'tms', 'wms', 'ims', 'cx', 'crm', 'iot', 'fleet', 'aegis', 'system', 'cruise'],
      features: {
        autoTender: true,
        aegisIntelligence: true,
        customerPortal: true,
        iotMonitoring: true,
        fleetManagement: true,
        voiceCalls: true,
      },
    },
    branding: {
      companyName: 'Kairos Demo',
      logoUrl: '',
      // Customer Zero (Kairos demo) intentionally keeps default Kairos branding.
      // Other tenants set a hex primaryColor via /settings/brand (WHITELABEL-1).
      primaryColor: 'var(--signal-gold)',
      portalDomain: 'demo.elyonsci.ai',
    },
    billingEmail: '',
    stripeCustomerId: '',
    trialEndsAt: null,
    softDeleted: false,
    createdBy: 'system',
  },
  {
    tenantId: WATCHMEN_TENANT_ID,
    name: 'Watchmen Logistics',
    status: 'active',
    plan: 'enterprise',
    primaryContactName: 'Randall Roberson',
    primaryContactEmail: 'randall@watchmenlogistics.com',
    primaryContactPhone: '',
    config: {
      timezone: 'America/Chicago',
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD',
      weightUnit: 'lbs',
      distanceUnit: 'miles',
      modules: ['overview', 'tms', 'wms', 'ims', 'cx', 'crm', 'iot', 'fleet', 'aegis', 'system'],
      features: {
        autoTender: true,
        aegisIntelligence: true,
        customerPortal: true,
        iotMonitoring: true,
        fleetManagement: true,
        voiceCalls: true,
      },
    },
    branding: {
      companyName: 'Watchmen Logistics',
      logoUrl: '',
      primaryColor: 'var(--signal-gold)',
      portalDomain: 'watchmen.elyonsci.ai',
    },
    billingEmail: '',
    stripeCustomerId: '',
    trialEndsAt: null,
    softDeleted: false,
    createdBy: 'system',
  },
  // NCL-SEED-DATA-ACTIVATION-1: Norwegian Cruise Line tenant for ?tenant=ncl dev mode
  {
    tenantId: 'ncl',
    name: 'Norwegian Cruise Line',
    status: 'active',
    plan: 'enterprise',
    primaryContactName: 'NCL Provisioning Manager',
    primaryContactEmail: 'provisioning@ncl.com',
    primaryContactPhone: '',
    enabled_modules: ['ncl', 'cruise', 'tms', 'wms', 'ims', 'fleet', 'aegis', 'overview'],
    config: {
      timezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD',
      weightUnit: 'lbs',
      distanceUnit: 'miles',
      modules: {
        enabled: {
          ncl: true, cruise: true, tms: true, wms: true,
          ims: true, fleet: true, aegis: true, overview: true,
        },
      },
      features: {
        aegisIntelligence: true,
        cruiseProvisioning: true,
        fleetManagement: true,
      },
    },
    branding: {
      companyName: 'Norwegian Cruise Line',
      logoUrl: '',
      primaryColor: 'var(--color-primary-900)',
      portalDomain: 'ncl.elyonsci.ai',
    },
    billingEmail: '',
    stripeCustomerId: '',
    trialEndsAt: null,
    softDeleted: false,
    createdBy: 'system',
  },
];

// NCL-SEED-DATA-ACTIVATION-1: tenant public mirror records (tenants_public collection)
const SEED_TENANTS_PUBLIC = [
  {
    id: 'ncl',
    tenantId: 'ncl',
    display_name: 'Norwegian Cruise Line',
    status: 'active',
    brand_id: 'ncl',
    modules_enabled: ['ncl', 'cruise', 'tms', 'wms', 'ims', 'fleet', 'aegis', 'overview'],
    logo_url: '',
    primary_color_token: 'var(--color-primary-900)',
    softDeleted: false,
  },
];

// 21 NCL fleet ships — brand_id must match NCLFleetShipSelector's BRAND_ORDER entries.
const SEED_NCL_FLEET_SHIPS = [
  { ship_id: 'ncl-001', name: 'Norwegian Joy',         brand_id: 'ncl-brand',     ship_class: 'Breakaway Plus', imo_number: '9677076', grossTonnage: 167800, homePort: 'New York',        status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-001' },
  { ship_id: 'ncl-002', name: 'Norwegian Bliss',        brand_id: 'ncl-brand',     ship_class: 'Breakaway Plus', imo_number: '9751509', grossTonnage: 168028, homePort: 'Seattle',         status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-002' },
  { ship_id: 'ncl-003', name: 'Norwegian Encore',       brand_id: 'ncl-brand',     ship_class: 'Breakaway Plus', imo_number: '9776597', grossTonnage: 169145, homePort: 'Miami',           status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-003' },
  { ship_id: 'ncl-004', name: 'Norwegian Prima',        brand_id: 'ncl-brand',     ship_class: 'Prima',          imo_number: '9837444', grossTonnage: 142500, homePort: 'New York',        status: 'docked',  active: true, current_sailing_id: null },
  { ship_id: 'ncl-005', name: 'Norwegian Viva',         brand_id: 'ncl-brand',     ship_class: 'Prima',          imo_number: '9883574', grossTonnage: 142500, homePort: 'Miami',           status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-005' },
  { ship_id: 'ncl-006', name: 'Norwegian Escape',       brand_id: 'ncl-brand',     ship_class: 'Breakaway Plus', imo_number: '9586106', grossTonnage: 164998, homePort: 'New York',        status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-006' },
  { ship_id: 'ncl-007', name: 'Norwegian Breakaway',    brand_id: 'ncl-brand',     ship_class: 'Breakaway',      imo_number: '9550945', grossTonnage: 146600, homePort: 'New York',        status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-007' },
  { ship_id: 'ncl-008', name: 'Norwegian Getaway',      brand_id: 'ncl-brand',     ship_class: 'Breakaway',      imo_number: '9606912', grossTonnage: 145655, homePort: 'Miami',           status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-008' },
  { ship_id: 'ncl-009', name: 'Norwegian Dawn',         brand_id: 'ncl-brand',     ship_class: 'Dawn',           imo_number: '9217009', grossTonnage: 92250,  homePort: 'Boston',          status: 'docked',  active: true, current_sailing_id: null },
  { ship_id: 'ncl-010', name: 'Norwegian Star',         brand_id: 'ncl-brand',     ship_class: 'Dawn',           imo_number: '9195169', grossTonnage: 91740,  homePort: 'Los Angeles',     status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-010' },
  { ship_id: 'ncl-011', name: 'Norwegian Sun',          brand_id: 'ncl-brand',     ship_class: 'Sun',            imo_number: '9191009', grossTonnage: 78309,  homePort: 'Miami',           status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-011' },
  { ship_id: 'ncl-012', name: 'Norwegian Sky',          brand_id: 'ncl-brand',     ship_class: 'Sky',            imo_number: '9128532', grossTonnage: 77104,  homePort: 'Miami',           status: 'at_sea',  active: true, current_sailing_id: 'SAIL-NCL-012' },
  { ship_id: 'oce-001', name: 'Riviera',                brand_id: 'oceania-brand', ship_class: 'Oceania R',      imo_number: '9438078', grossTonnage: 66084,  homePort: 'Miami',           status: 'at_sea',  active: true, current_sailing_id: 'SAIL-OCE-001' },
  { ship_id: 'oce-002', name: 'Marina',                 brand_id: 'oceania-brand', ship_class: 'Oceania R',      imo_number: '9438066', grossTonnage: 66084,  homePort: 'Miami',           status: 'at_sea',  active: true, current_sailing_id: 'SAIL-OCE-002' },
  { ship_id: 'oce-003', name: 'Vista',                  brand_id: 'oceania-brand', ship_class: 'Allura',         imo_number: '9892553', grossTonnage: 67000,  homePort: 'Miami',           status: 'at_sea',  active: true, current_sailing_id: 'SAIL-OCE-003' },
  { ship_id: 'oce-004', name: 'Nautica',                brand_id: 'oceania-brand', ship_class: 'Oceania R-Clss', imo_number: '9218131', grossTonnage: 30277,  homePort: 'Miami',           status: 'docked',  active: true, current_sailing_id: null },
  { ship_id: 'oce-005', name: 'Sirena',                 brand_id: 'oceania-brand', ship_class: 'Oceania R-Clss', imo_number: '9219763', grossTonnage: 30277,  homePort: 'Miami',           status: 'at_sea',  active: true, current_sailing_id: 'SAIL-OCE-005' },
  { ship_id: 'reg-001', name: 'Seven Seas Grandeur',    brand_id: 'regent-brand',  ship_class: 'Regent G',       imo_number: '9888932', grossTonnage: 55254,  homePort: 'Fort Lauderdale', status: 'at_sea',  active: true, current_sailing_id: 'SAIL-REG-001' },
  { ship_id: 'reg-002', name: 'Seven Seas Splendor',    brand_id: 'regent-brand',  ship_class: 'Regent S',       imo_number: '9805497', grossTonnage: 55254,  homePort: 'Fort Lauderdale', status: 'at_sea',  active: true, current_sailing_id: 'SAIL-REG-002' },
  { ship_id: 'reg-003', name: 'Seven Seas Explorer',    brand_id: 'regent-brand',  ship_class: 'Regent E',       imo_number: '9686594', grossTonnage: 54900,  homePort: 'Fort Lauderdale', status: 'docked',  active: true, current_sailing_id: null },
  { ship_id: 'reg-004', name: 'Seven Seas Mariner',     brand_id: 'regent-brand',  ship_class: 'Regent M',       imo_number: '9227510', grossTonnage: 48075,  homePort: 'Fort Lauderdale', status: 'at_sea',  active: true, current_sailing_id: 'SAIL-REG-004' },
];

function seedNCLFleetShipsIfMissing() {
  const key = 'wc_fleet/ships';
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  // Wipe and re-seed if brand_id schema changed (legacy records used 'ncl' not 'ncl-brand').
  const needsReset = existing.length > 0 && existing[0]?.brand_id !== 'ncl-brand';
  if (needsReset) localStorage.removeItem(key);
  const current = needsReset ? [] : existing;
  const byId = new Map(current.map(s => [s.ship_id, s]));
  let added = 0;
  const now = new Date().toISOString();
  for (const ship of SEED_NCL_FLEET_SHIPS) {
    if (!byId.has(ship.ship_id)) {
      current.push({ ...ship, id: ship.ship_id, tenantId: 'ncl', isDeleted: false, createdAt: now, updatedAt: now });
      added++;
    }
  }
  if (added > 0) {
    localStorage.setItem(key, JSON.stringify(current));
    console.log(`[SeedLoader] Seeded fleet/ships: ${added} NCL ship records`);
  }
}

function seedTenantsPublicIfMissing() {
  const existing = JSON.parse(localStorage.getItem('wc_tenants_public') || '[]');
  const byId = new Map(existing.map(t => [t.id, t]));
  let added = 0;
  for (const rec of SEED_TENANTS_PUBLIC) {
    if (!byId.has(rec.id)) {
      existing.push({ ...rec });
      added++;
    }
  }
  if (added > 0) {
    localStorage.setItem('wc_tenants_public', JSON.stringify(existing));
    console.log(`[SeedLoader] Seeded tenants_public: ${added} records`);
  }
}

// ── Seed notifications ──
const daysAgo = (d) => new Date(Date.now() - d * 86400000).toISOString();
const hoursAgo = (h) => new Date(Date.now() - h * 3600000).toISOString();
const SEED_NOTIFICATIONS = [
  { id: 'notif-001', userId: 'user-001', type: 'exception', priority: 'critical', title: 'Delay: Mechanical Breakdown', message: 'Load WC-10712 delayed at Louisville, KY — NS railroad mechanical issue', loadId: 'load-00013', actionUrl: '/tms/rail-tower', isRead: false, readAt: null, emailSent: true, emailSentAt: hoursAgo(2), createdAt: hoursAgo(2), createdBy: 'system', updatedAt: hoursAgo(2), updatedBy: 'system', isDeleted: false, version: 1, source: 'trigger' },
  { id: 'notif-002', userId: 'user-001', type: 'iot_alert', priority: 'critical', title: 'Temperature Breach: 78.4°F', message: 'Tracker WM-TRK-01003 on load WC-10703 — temperature above 72°F threshold', loadId: 'load-00003', actionUrl: '/iot/map', isRead: false, readAt: null, emailSent: true, emailSentAt: hoursAgo(4), createdAt: hoursAgo(4), createdBy: 'system', updatedAt: hoursAgo(4), updatedBy: 'system', isDeleted: false, version: 1, source: 'trigger' },
  { id: 'notif-003', userId: 'user-001', type: 'lfd_warning', priority: 'critical', title: 'LFD Alert: 18h remaining', message: 'Container CSLU2145678 at Pier J Long Beach — Last Free Day approaching', loadId: 'dray-002', actionUrl: '/tms/drayage-tower', isRead: false, readAt: null, emailSent: true, emailSentAt: hoursAgo(6), createdAt: hoursAgo(6), createdBy: 'system', updatedAt: hoursAgo(6), updatedBy: 'system', isDeleted: false, version: 1, source: 'trigger' },
  { id: 'notif-004', userId: 'user-001', type: 'customs_hold', priority: 'high', title: 'Customs Hold Placed', message: 'WC-OCN-006 placed on customs hold at Savannah — pharmaceutical cargo', loadId: 'ocn-006', actionUrl: '/tms/ocean-tower', isRead: false, readAt: null, emailSent: true, emailSentAt: hoursAgo(8), createdAt: hoursAgo(8), createdBy: 'system', updatedAt: hoursAgo(8), updatedBy: 'system', isDeleted: false, version: 1, source: 'trigger' },
  { id: 'notif-005', userId: 'user-001', type: 'lfd_warning', priority: 'high', title: 'LFD Alert: 36h remaining', message: 'Container TCKU3456789 at TraPac Terminal — Last Free Day in 36 hours', loadId: 'dray-003', actionUrl: '/tms/drayage-tower', isRead: false, readAt: null, emailSent: false, emailSentAt: null, createdAt: hoursAgo(10), createdBy: 'system', updatedAt: hoursAgo(10), updatedBy: 'system', isDeleted: false, version: 1, source: 'trigger' },
  { id: 'notif-006', userId: 'user-001', type: 'quote_approved', priority: 'medium', title: 'Quote Approved', message: 'QT-10025 for Pacific Fresh Foods approved — $3,200 OTR Chicago to Dallas', loadId: null, actionUrl: '/tms/quotes', isRead: false, readAt: null, emailSent: false, emailSentAt: null, createdAt: hoursAgo(12), createdBy: 'system', updatedAt: hoursAgo(12), updatedBy: 'system', isDeleted: false, version: 1, source: 'trigger' },
  { id: 'notif-007', userId: 'user-001', type: 'aegis_insight', priority: 'medium', title: 'AEGIS Insight', message: 'Carrier SWFT on-time rate dropped to 82% this week — 3 delays on Chicago lanes', loadId: null, actionUrl: '/aegis', isRead: false, readAt: null, emailSent: false, emailSentAt: null, createdAt: hoursAgo(14), createdBy: 'aegis', updatedAt: hoursAgo(14), updatedBy: 'aegis', isDeleted: false, version: 1, source: 'aegis' },
  { id: 'notif-008', userId: 'user-001', type: 'load_delivered', priority: 'low', title: 'Load Delivered', message: 'WC-10745 delivered to Charlotte, NC — Pinnacle Construction', loadId: 'load-00046', actionUrl: '/tms/loadboard', isRead: true, readAt: hoursAgo(1), emailSent: false, emailSentAt: null, createdAt: hoursAgo(18), createdBy: 'system', updatedAt: hoursAgo(1), updatedBy: 'user-001', isDeleted: false, version: 2, source: 'trigger' },
  { id: 'notif-009', userId: 'user-001', type: 'load_delivered', priority: 'low', title: 'Load Delivered', message: 'WC-10738 delivered to Memphis, TN — MedSupply Direct', loadId: 'load-00039', actionUrl: '/tms/loadboard', isRead: true, readAt: hoursAgo(2), emailSent: false, emailSentAt: null, createdAt: hoursAgo(22), createdBy: 'system', updatedAt: hoursAgo(2), updatedBy: 'user-001', isDeleted: false, version: 2, source: 'trigger' },
  { id: 'notif-010', userId: 'user-001', type: 'exception', priority: 'high', title: 'Detention Alert', message: 'Load WC-10721 detained at shipper for 4+ hours — Heartland Grain Co facility', loadId: 'load-00022', actionUrl: '/tms/tracking', isRead: true, readAt: hoursAgo(3), emailSent: true, emailSentAt: hoursAgo(26), createdAt: hoursAgo(26), createdBy: 'system', updatedAt: hoursAgo(3), updatedBy: 'user-001', isDeleted: false, version: 2, source: 'trigger' },
  { id: 'notif-011', userId: 'user-001', type: 'aegis_insight', priority: 'medium', title: 'AEGIS Recommendation', message: 'Consider consolidating 3 LTL shipments to Newark — potential $1,200 savings', loadId: null, actionUrl: '/aegis', isRead: true, readAt: hoursAgo(5), emailSent: false, emailSentAt: null, createdAt: hoursAgo(30), createdBy: 'aegis', updatedAt: hoursAgo(5), updatedBy: 'user-001', isDeleted: false, version: 2, source: 'aegis' },
  { id: 'notif-012', userId: 'user-001', type: 'system', priority: 'low', title: 'Carrier Insurance Expiring', message: 'USA Truck (USAT) insurance expires in 15 days — review before next dispatch', loadId: null, actionUrl: '/tms/carriers', isRead: true, readAt: hoursAgo(8), emailSent: false, emailSentAt: null, createdAt: hoursAgo(48), createdBy: 'system', updatedAt: hoursAgo(8), updatedBy: 'user-001', isDeleted: false, version: 2, source: 'trigger' },
];

const COLLECTIONS = [
  { name: 'customers', data: [...EXPANDED_CUSTOMERS, ...SEED_VOICE_CUSTOMERS] },
  { name: 'carriers', data: EXPANDED_CARRIERS },
  { name: 'loads', data: [...EXPANDED_LOADS, ...SEED_VOICE_LOADS, ...SEED_MULTIMODAL_LOADS, ...SEED_RIVERSIDE_LOADS, ...SEED_QUOTING_HUNGER_LOADS] },
  { name: 'quotes', data: [...EXPANDED_QUOTES, ...SEED_QUOTING_QUOTES] },
  // TMS-AUTOSEED-SEAT-F-PARITY-1 — TMS-C3 spot bid chain auto-seed
  { name: 'spot_bids', data: SEED_SPOT_BIDS },
  { name: 'purchaseOrders', data: [...EXPANDED_PURCHASE_ORDERS, ...SEED_VOICE_PURCHASE_ORDERS] },
  { name: 'appointments', data: EXPANDED_APPOINTMENTS },
  { name: 'carrierVetting', data: EXPANDED_CARRIER_VETTING },
  { name: 'iot_trackers', data: EXPANDED_IOT_TRACKERS },
  { name: 'iot_events', data: EXPANDED_IOT_EVENTS },
  { name: 'assets', data: EXPANDED_ASSETS },
  { name: 'trucks', data: EXPANDED_TRUCKS },
  { name: 'financials', data: EXPANDED_FINANCIALS },
  { name: 'events', data: EXPANDED_EVENTS },
  { name: 'exceptions', data: [...EXPANDED_EXCEPTIONS, ...SEED_VOICE_EXCEPTIONS] },
  { name: 'inventory', data: SEED_VOICE_INVENTORY },
  { name: 'notifications', data: SEED_NOTIFICATIONS },
  // Fleet Management (FLEET-1)
  { name: 'fleet_vehicles', data: SEED_FLEET_VEHICLES },
  { name: 'fleet_drivers', data: SEED_FLEET_DRIVERS },
  { name: 'fleet_maintenance', data: SEED_FLEET_MAINTENANCE },
  { name: 'fleet_compliance', data: SEED_FLEET_COMPLIANCE },
  // AEGIS-7 platform intelligence
  { name: 'aegis_actions', data: SEED_AEGIS_ACTIONS },
  { name: 'aegis_predictions', data: SEED_AEGIS_PREDICTIONS },
  // AEGIS-8 multi-agent swarm
  { name: 'aegis_agents', data: SEED_AEGIS_AGENTS },
  { name: 'aegis_agent_events', data: SEED_AEGIS_AGENT_EVENTS },
  { name: 'aegis_negotiations', data: SEED_AEGIS_NEGOTIATIONS },
  // AEGIS-9 voice-native operations
  { name: 'voice_calls', data: SEED_VOICE_CALLS },
  // AEGIS-11 geopolitical + tariff risk
  { name: 'aegis_risk_events', data: SEED_AEGIS_RISK_EVENTS },
  { name: 'aegis_country_risk', data: SEED_AEGIS_COUNTRY_RISK },
  // AEGIS-12 team copilot + performance intelligence
  { name: 'aegis_user_activity', data: SEED_AEGIS_USER_ACTIVITY },
  { name: 'aegis_performance_profiles', data: SEED_AEGIS_PERFORMANCE_PROFILES },
  { name: 'aegis_coaching_insights', data: SEED_AEGIS_COACHING_INSIGHTS },
  { name: 'aegis_performance_goals', data: SEED_AEGIS_PERFORMANCE_GOALS },
  // TMS-NQL — New Quote & Load
  { name: 'carrier_compliance', data: SEED_CARRIER_COMPLIANCE },
  { name: 'quote_outcomes', data: SEED_QUOTE_OUTCOMES },
  { name: 'quote_templates', data: SEED_QUOTE_TEMPLATES },
  // DOC-1 / DOC-2 — AEGIS document intelligence
  { name: 'doc_parse_jobs', data: [...SEED_DOC_PARSE_JOBS, ...SEED_KEYSTON_PARSE_JOBS] },
  { name: 'doc_parse_results', data: [...SEED_DOC_PARSE_RESULTS, ...SEED_KEYSTON_PARSE_RESULTS] },
  { name: 'doc_feedback', data: SEED_DOC_FEEDBACK },
  // DOC-3 — Riverside NF dock-to-broker
  { name: 'wms_picks', data: SEED_RIVERSIDE_PICKS },
  // TMS-2 through TMS-6 operations suite
  { name: 'rate_confirmations', data: [...SEED_RATE_CONFIRMATIONS, ...SEED_QUOTING_RATE_CONFIRMATIONS] },
  { name: 'carrier_invoices', data: [...SEED_CARRIER_INVOICES, ...SEED_QUOTING_INVOICES] },
  { name: 'disputes', data: SEED_DISPUTES },
  { name: 'consolidation_opportunities', data: [...SEED_CONSOLIDATION_OPPORTUNITIES, ...SEED_CONSOLIDATION_ENGINE_OPPS] },
  // DIFF-1: AEGIS Autonomous Consolidation Engine
  { name: 'consolidation_outcomes', data: SEED_CONSOLIDATION_OUTCOMES },
  // DIFF-2: Three-Tier Autonomy Dispatch
  { name: 'dispatch_autonomy_rules', data: SEED_DISPATCH_AUTONOMY_RULES },
  { name: 'agent_dispatch_log', data: SEED_AGENT_DISPATCH_LOG },
  // DIFF-3: Carrier Vetting + Trust Intelligence
  { name: 'carrier_trust_scores', data: SEED_CARRIER_TRUST_SCORES },
  { name: 'carrier_alerts', data: SEED_CARRIER_ALERTS },
  { name: 'carrier_vetting_history', data: SEED_CARRIER_VETTING_HISTORY },
  // DIFF-4: Agentic Shipment Status
  { name: 'exception_predictions', data: SEED_EXCEPTION_PREDICTIONS },
  { name: 'proactive_notifications', data: SEED_PROACTIVE_NOTIFICATIONS },
  // DIFF-5: Daily Operations Intelligence
  { name: 'daily_ops_briefs', data: SEED_DAILY_OPS_BRIEFS },
  // DIFF-7: Customs + Tariff Impact Engine
  { name: 'hs_classifications', data: SEED_HS_CLASSIFICATIONS },
  { name: 'tariff_alerts', data: SEED_TARIFF_ALERTS },
  { name: 'landed_cost_snapshots', data: SEED_LANDED_COST_SNAPSHOTS },
  { name: 'emissions_factors', data: SEED_EMISSIONS_FACTORS },
  { name: 'emissions_reports', data: SEED_EMISSIONS_REPORTS },
  { name: 'scenarios', data: SEED_SCENARIOS },
  { name: 'customs_documents', data: SEED_CUSTOMS_DOCUMENTS },
  // TMS-7 through TMS-12 carrier & customer suite
  { name: 'carrier_allocation_rules', data: SEED_CARRIER_ALLOCATION_RULES },
  { name: 'report_schedules', data: SEED_REPORT_SCHEDULES },
  { name: 'report_runs', data: SEED_REPORT_RUNS },
  { name: 'edi_messages', data: SEED_EDI_MESSAGES },
  { name: 'carrier_onboarding_applications', data: SEED_CARRIER_ONBOARDING_APPLICATIONS },
  { name: 'loadboard_postings', data: SEED_LOADBOARD_POSTINGS },
  { name: 'loadboard_quotes', data: SEED_LOADBOARD_QUOTES },
  { name: 'carrier_rate_tables', data: [...SEED_CARRIER_RATE_TABLES, ...SEED_QUOTING_RATE_TABLES] },
  { name: 'carrier_contracts', data: SEED_CARRIER_CONTRACTS },
  { name: 'rate_confirmations_tms11', data: SEED_RATE_CONFIRMATIONS_TMS11 },
  { name: 'ltl_rate_quotes', data: SEED_LTL_RATE_QUOTES },
  { name: 'ltl_pro_tracking', data: SEED_LTL_PRO_TRACKING },
  // CX-5/6/7
  { name: 'nps_surveys', data: SEED_NPS_SURVEYS },
  { name: 'csat_surveys', data: SEED_CSAT_SURVEYS },
  { name: 'customer_onboarding_applications', data: SEED_CUSTOMER_ONBOARDING_APPS },
  { name: 'customer_contracts', data: SEED_CUSTOMER_CONTRACTS },
  // CRM (Overnight #7)
  { name: 'crm_accounts', data: SEED_CRM_ACCOUNTS },
  { name: 'crm_contacts', data: SEED_CRM_CONTACTS },
  { name: 'crm_deals', data: SEED_CRM_DEALS },
  { name: 'crm_activities', data: SEED_CRM_ACTIVITIES },
  { name: 'crm_demo_sends', data: SEED_CRM_DEMO_SENDS },
  { name: 'crm_demo_engagement', data: SEED_CRM_DEMO_ENGAGEMENT },
  { name: 'crm_prospect_mirrors', data: SEED_CRM_PROSPECT_MIRRORS },
  // INT-1 — Integration Hub foundation
  { name: 'connectors', data: SEED_CONNECTORS },
  { name: 'connector_sync_logs', data: SEED_CONNECTOR_SYNC_LOGS },
  { name: 'connector_field_maps', data: SEED_CONNECTOR_FIELD_MAPS },
  // AGENT-CRUISE — Cruise & Expedition Intelligence Pack
  { name: 'cruise_sailings',            data: SEED_CRUISE_SAILINGS },
  { name: 'cruise_recipes',             data: SEED_CRUISE_RECIPES },
  { name: 'cruise_consumption',         data: SEED_CRUISE_CONSUMPTION },
  { name: 'cruise_provisioning_plans',  data: SEED_CRUISE_PROVISIONING_PLANS },
  { name: 'marpol_records',             data: SEED_MARPOL_RECORDS },
  // AGENT-MFG — Manufacturing-to-Retail Intelligence Pack
  { name: 'retailer_compliance_profiles', data: SEED_RETAILER_COMPLIANCE_PROFILES },
  // AGENT-SPEC — Specifier Intelligence Pack
  { name: 'specifier_projects',         data: SEED_SPECIFIER_PROJECTS },
];

function seedTenantsIfMissing() {
  const existing = JSON.parse(localStorage.getItem('wc_tenants') || '[]');
  const byId = new Map(existing.map(t => [t.tenantId, t]));
  let added = 0;
  for (const tenant of SEED_TENANTS) {
    if (!byId.has(tenant.tenantId)) {
      const now = new Date().toISOString();
      existing.push({ ...tenant, id: tenant.tenantId, createdAt: now, updatedAt: now });
      added++;
    }
  }
  if (added > 0) {
    localStorage.setItem('wc_tenants', JSON.stringify(existing));
    console.log(`[SeedLoader] Seeded tenants: ${added} records`);
  }
}

export function loadSeedData() {
  const storedVersion = Number(localStorage.getItem(SEED_VERSION_KEY) || '0');

  // Skip the entire 90-collection loop on every mount when already at the current
  // version. Without this guard the loop runs unconditionally on every page load
  // (main.jsx:25 calls loadSeedData() with no outer check), burning ~90 localStorage
  // reads for nothing. If a user clears their data they can clear wc_seed_version
  // as well to force a full reseed. The per-deploy data-wipe issue (FORCE-RESEED-
  // PRESERVE-USER-DATA-1) is tracked separately and is out of scope here.
  if (storedVersion === SEED_VERSION) return;

  let seeded = 0;
  let skipped = 0;

  const forceReseed = storedVersion < SEED_VERSION;
  if (forceReseed && storedVersion > 0) {
    console.log(`[SeedLoader] Seed version bumped ${storedVersion} → ${SEED_VERSION}; clearing demo records`);
    for (const { name } of COLLECTIONS) {
      if (FORCE_RESEED_ALLOWED.has(name)) localStorage.removeItem('wc_' + name);
    }
  }

  seedTenantsIfMissing();
  seedTenantsPublicIfMissing();
  seedNCLFleetShipsIfMissing();

  for (const { name, data } of COLLECTIONS) {
    const key = 'wc_' + name;
    const existing = localStorage.getItem(key);

    if (!existing || JSON.parse(existing).length === 0) {
      const records = data.map(d => ({ ...d, tenantId: DEMO_TENANT_ID }));
      localStorage.setItem(key, JSON.stringify(records));
      console.log(`[SeedLoader] Seeded ${name}: ${records.length} records`);
      seeded++;
    } else {
      skipped++;
    }
  }

  if (forceReseed || !localStorage.getItem('wc_sub_aegis_agents__carrier_agent__memory')) {
    const byAgent = new Map();
    SEED_AGENT_MEMORY.forEach(row => {
      const key = row.agentMemoryParent.replace(/\//g, '__');
      if (!byAgent.has(key)) byAgent.set(key, []);
      // eslint-disable-next-line no-unused-vars
      const { agentMemoryParent, ...rest } = row;
      byAgent.get(key).push({ ...rest, tenantId: DEMO_TENANT_ID });
    });
    byAgent.forEach((rows, key) => {
      localStorage.setItem('wc_sub_' + key, JSON.stringify(rows));
    });
    console.log(`[SeedLoader] Seeded agent memory into ${byAgent.size} subcollection buckets`);
  }

  // REG-AUTO-1: compliance subcollection seed (regulatory feeds, exposures, filings)
  seedComplianceData();

  localStorage.setItem(SEED_VERSION_KEY, String(SEED_VERSION));

  if (seeded > 0) {
    console.log(`[SeedLoader] Complete — seeded ${seeded} collections, skipped ${skipped}`);
    console.table(SEED_SUMMARY);
  }
}

/**
 * Clear all seed data from localStorage (for reset).
 */
export function clearSeedData() {
  for (const { name } of COLLECTIONS) {
    localStorage.removeItem('wc_' + name);
  }
  console.log('[SeedLoader] All seed data cleared');
}

// ── Historical seed (Session 18B) ────────────────────────────────────
import { generateAllHistorical, HISTORICAL_SUMMARY } from './historicalSeed';
import DataService from '../services/DataService';

const HISTORICAL_COLLECTIONS = [
  'historical_loads',
  'historical_exceptions',
  'historical_demand',
  'historical_check_calls',
  'wms_receiving',
  'wms_putaway',
  'wms_picks',
  'wms_labor',
];

export function isHistoricalSeedLoaded() {
  return HISTORICAL_COLLECTIONS.some(name => {
    try {
      const rows = JSON.parse(localStorage.getItem('wc_' + name) || '[]');
      return Array.isArray(rows) && rows.length > 0;
    } catch { return false; }
  });
}

export async function loadHistoricalSeed({ useFirestore = false, onProgress } = {}) {
  const dataByCollection = generateAllHistorical();

  // HISTORICAL-SEED-FOLLOWUP-1: symmetric gate with `loadCxSeed`. The previous
  // `useFirestore || isConfigured` path silently routed writes to Firestore
  // whenever Firebase was configured, which is the same shape that produced
  // the 90× permission cascade fixed under VIEW-MODE-CLUSTER-HOTFIX-1 (Bug 2).
  // The `historical_*` and `wms_*` collections likewise have no firestore.rules
  // entries and would default-deny on a privileged caller. Require explicit
  // opt-in via `useFirestore: true`; auto-init / window-hook callers stay in
  // localStorage. The privileged setup path remains available for the future
  // admin-SDK seeder under VIEW-MODE-CLUSTER-HOTFIX-1-FOLLOWUP-1.
  const writeViaFirestore = useFirestore === true;

  const summary = {};

  if (!writeViaFirestore) {
    for (const name of HISTORICAL_COLLECTIONS) {
      const rows = dataByCollection[name] || [];
      localStorage.setItem('wc_' + name, JSON.stringify(rows));
      summary[name] = rows.length;
      if (onProgress) onProgress({ collection: name, done: rows.length, total: rows.length });
    }
    console.log('[SeedLoader] Historical seed loaded to localStorage');
    console.table({ ...HISTORICAL_SUMMARY.targetVolumes, ...summary });
    return summary;
  }

  for (const name of HISTORICAL_COLLECTIONS) {
    const rows = dataByCollection[name] || [];
    summary[name] = 0;
    for (let i = 0; i < rows.length; i++) {
      const { id, ...payload } = rows[i]; // eslint-disable-line no-unused-vars
      await DataService.create(name, payload, 'seed', 'historical_seed');
      summary[name]++;
      if (onProgress && (i % 25 === 0 || i === rows.length - 1)) {
        onProgress({ collection: name, done: i + 1, total: rows.length });
      }
    }
  }

  console.log('[SeedLoader] Historical seed loaded to Firestore');
  console.table({ ...HISTORICAL_SUMMARY.targetVolumes, ...summary });
  return summary;
}

export function clearHistoricalSeed() {
  for (const name of HISTORICAL_COLLECTIONS) {
    localStorage.removeItem('wc_' + name);
  }
  console.log('[SeedLoader] Historical seed cleared from localStorage');
}

if (typeof window !== 'undefined') {
  window.loadHistoricalSeed = loadHistoricalSeed;
  window.clearHistoricalSeed = clearHistoricalSeed;
  window.isHistoricalSeedLoaded = isHistoricalSeedLoaded;
}

// ── CX Data Foundation (Session 19A) ─────────────────────────────────────────
import {
  SEED_CX_CASES,
  SEED_CX_TEMPLATES,
  SEED_CUSTOMER_CONTACTS,
  SEED_CX_MESSAGES,
  SEED_SURVEYS,
  CX_SEED_SUMMARY,
} from './seedCxData';

const CX_COLLECTIONS = [
  { name: 'cx_cases',           data: SEED_CX_CASES },
  { name: 'cx_templates',       data: SEED_CX_TEMPLATES },
  { name: 'customer_contacts',  data: SEED_CUSTOMER_CONTACTS },
  { name: 'cx_messages',        data: SEED_CX_MESSAGES },
  { name: 'surveys',            data: SEED_SURVEYS },
];

export function isCxSeedLoaded() {
  return CX_COLLECTIONS.some(({ name }) => {
    try {
      const rows = JSON.parse(localStorage.getItem('wc_' + name) || '[]');
      return Array.isArray(rows) && rows.length > 0;
    } catch { return false; }
  });
}

export async function loadCxSeed({ useFirestore = false, force = false, onProgress } = {}) {
  if (!force && isCxSeedLoaded()) {
    console.log('[SeedLoader] CX seed already loaded — skipping (pass { force: true } to override)');
    return {};
  }

  // VIEW-MODE-CLUSTER-HOTFIX-1 (Bug 2): the previous gate `useFirestore ||
  // isConfigured` made auto-init writes to Firestore whenever Firebase was
  // configured — which is always true in production. The CX seed collections
  // (cx_cases, cx_templates, customer_contacts, surveys) have no firestore.rules
  // entries and fall through to default-deny, so 90 of 92 writes silently
  // failed with "Missing or insufficient permissions". Until a privileged
  // setup script seeds these via the admin SDK, only opt-in (explicit
  // `useFirestore: true`) callers may attempt the Firestore write path. The
  // default app-init path stays in localStorage.
  const writeViaFirestore = useFirestore === true;
  const summary = {};

  if (!writeViaFirestore) {
    for (const { name, data } of CX_COLLECTIONS) {
      const records = data.map(d => ({ ...d, tenantId: DEMO_TENANT_ID }));
      localStorage.setItem('wc_' + name, JSON.stringify(records));
      summary[name] = records.length;
      if (onProgress) onProgress({ collection: name, done: records.length, total: records.length });
      console.log(`[SeedLoader] CX seeded ${name}: ${records.length} records`);
    }
    console.log('[SeedLoader] CX seed loaded to localStorage');
    console.table(CX_SEED_SUMMARY);
    return summary;
  }

  for (const { name, data } of CX_COLLECTIONS) {
    summary[name] = 0;
    for (let i = 0; i < data.length; i++) {
      const { id, ...payload } = data[i]; // eslint-disable-line no-unused-vars
      await DataService.create(name, { ...payload, tenantId: DEMO_TENANT_ID }, 'seed', 'cx_seed');
      summary[name]++;
      if (onProgress && (i % 5 === 0 || i === data.length - 1)) {
        onProgress({ collection: name, done: i + 1, total: data.length });
      }
    }
  }

  console.log('[SeedLoader] CX seed loaded to Firestore');
  console.table(CX_SEED_SUMMARY);
  return summary;
}

export function clearCxSeed() {
  for (const { name } of CX_COLLECTIONS) {
    localStorage.removeItem('wc_' + name);
  }
  console.log('[SeedLoader] CX seed cleared from localStorage');
}

if (typeof window !== 'undefined') {
  window.loadCxSeed = loadCxSeed;
  window.clearCxSeed = clearCxSeed;
  window.isCxSeedLoaded = isCxSeedLoaded;
}